import { Treinserie } from "@/entities/opstartplan";
import { OpstartplanActieDto } from "@/shared/types";
import { parseISO } from "date-fns";
import { ActieDtoTransformatieError } from "./actie-error";
import { BaseActie } from "./base-actie";

export class OpstartplanActieTransformatieError extends ActieDtoTransformatieError {
    private constructor(wijzigingType: BaseActie["wijzigingType"], reason: string) {
        super("Opstartplan", wijzigingType, reason);
    }

    public static geenNieuweWaardeVoorToegevoegd(): OpstartplanActieTransformatieError {
        return new OpstartplanActieTransformatieError("Toegevoegd", `nieuweWaarde vereist is, maar deze is: nieuweWaarde=null`);
    }

    public static geenNieuweWaardeVoorGewijzigd(): OpstartplanActieTransformatieError {
        return new OpstartplanActieTransformatieError("Gewijzigd", "nieuweWaarde is vereist, maar deze mist");
    }
}

export type OpstartplanToegevoegdActie = BaseActie & {
    type: "Opstartplan";
    wijzigingType: "Toegevoegd";
    nieuweWaarde: Treinserie[];
};

export const OpstartplanToegevoegdActie = {
    fromDto(dto: OpstartplanActieDto): OpstartplanToegevoegdActie {
        if (!dto.nieuweWaarde) {
            throw OpstartplanActieTransformatieError.geenNieuweWaardeVoorToegevoegd();
        }
        return {
            id: dto.id,
            type: "Opstartplan",
            timelineContent: {
                titel: "Opstartplan toevoegen",
                beschrijving: "Opstartplan is toegevoegd in Spoorweb",
                actieTekst: "Controleer en bevestig opstartplan",
            },
            dialogContent: {
                titel: "Opstartplan toegevoegd",
                beschrijving: "Het opstartplan is in spoorweb toegevoegd. Wil je deze update doorvoeren in het SEIN dossier?",
                weigerenTitel: "Opstartplan weigeren?",
                weigerenBeschrijving: "Weet je zeker dat je het opstartplan wilt weigeren?",
                weigerenActieTekst: "Opstartplan weigeren",
            },
            verstuurdOpDatumTijd: parseISO(dto.verstuurdOpDatumTijd),
            toelichting: dto.toelichting,
            toelichtingLabel: "Toelichting opstartplan",
            status: dto.status,
            wijzigingType: "Toegevoegd",
            nieuweWaarde: dto.nieuweWaarde.map(Treinserie.fromDto).sort(Treinserie.sort),
        };
    },
};

export type OpstartplanGewijzigdActie = BaseActie & {
    type: "Opstartplan";
    wijzigingType: "Gewijzigd";
    nieuweWaarde: Treinserie[];
};

export const OpstartplanGewijzigdActie = {
    fromDto(dto: OpstartplanActieDto): OpstartplanGewijzigdActie {
        if (!dto.nieuweWaarde) {
            throw OpstartplanActieTransformatieError.geenNieuweWaardeVoorGewijzigd();
        }
        return {
            id: dto.id,
            type: "Opstartplan",
            timelineContent: {
                titel: "Opstartplan wijzigen",
                beschrijving: "Opstartplan is gewijzigd in Spoorweb",
                actieTekst: "Controleer en bevestig opstartplan",
            },
            dialogContent: {
                titel: "Opstartplan gewijzigd",
                beschrijving: "Het opstartplan is in spoorweb gewijzigd. Wil je deze update doorvoeren in het SEIN dossier?",
                weigerenTitel: "Opstartplan weigeren?",
                weigerenBeschrijving: "Weet je zeker dat je het opstartplan wilt weigeren?",
                weigerenActieTekst: "Opstartplan weigeren",
            },
            verstuurdOpDatumTijd: parseISO(dto.verstuurdOpDatumTijd),
            toelichting: dto.toelichting,
            toelichtingLabel: "Toelichting opstartplan",
            status: dto.status,
            wijzigingType: "Gewijzigd",
            nieuweWaarde: dto.nieuweWaarde.map(Treinserie.fromDto).sort(Treinserie.sort),
        };
    },
};

export type OpstartplanActie = OpstartplanToegevoegdActie | OpstartplanGewijzigdActie;

export const OpstartplanActie = {
    fromDto: (dto: OpstartplanActieDto): OpstartplanActie => {
        switch (dto.wijzigingType) {
            case "Toegevoegd":
                return OpstartplanToegevoegdActie.fromDto(dto);
            case "Gewijzigd":
                return OpstartplanGewijzigdActie.fromDto(dto);
            default:
                throw ActieDtoTransformatieError.nietOndersteundWijzigingTypeVoorActie(dto.type, dto.wijzigingType);
        }
    },
    toDto: (actie: OpstartplanActie, toelichting: string): OpstartplanActieDto => ({
        id: actie.id,
        type: actie.type,
        verstuurdOpDatumTijd: actie.verstuurdOpDatumTijd.toISOString(),
        status: actie.status,
        toelichting: toelichting,
        nieuweWaarde: actie.nieuweWaarde.map(Treinserie.toDto) as OpstartplanActieDto["nieuweWaarde"],
        wijzigingType: actie.wijzigingType,
    }),
};
